import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";
import CrashTrackImpactIcon from "../../../assets/svg/crashtrack-impact.svg";
import svgIcons from "../../../assets/svg/svgIcons";
import { mapVehicleEventIconWrapped } from "../../../assets/svg/vehicle-event-wrapped";
import { mapVehicleIconWrapped } from "../../../assets/svg/vehicle-wrapped";
import { APP_CONFIG } from "../../../constants/constants";
import { TMPushpin } from "../type";
import { statusCodeIsPinned } from "../statusCodeFilter";
import { RenderRoute } from "./renderRoute.class";

const debug = false;

const MAX_VIAPOINTS = 10;

const HEADING_BUCKET_DEGREES = 5;

const parsedIconCache = new Map<string, Element>();
const sharedSvgParser = new DOMParser();

const CSS_PIN_SHADOW = "drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.32))";

const swapSvgShadowForCss = (root: Element) => {
  const filtered = root.querySelectorAll("[filter]");
  if (filtered.length === 0) return;
  filtered.forEach((node) => node.removeAttribute("filter"));
  root.querySelectorAll("filter").forEach((node) => node.remove());
  (root as unknown as HTMLElement).style.filter = CSS_PIN_SHADOW;
};

const parsedIconFor = (cacheKey: string, svgString: string): Element => {
  let template = parsedIconCache.get(cacheKey);
  if (!template) {
    template = sharedSvgParser.parseFromString(
      svgString,
      "image/svg+xml"
    ).documentElement;
    swapSvgShadowForCss(template);
    parsedIconCache.set(cacheKey, template);
  }
  return template.cloneNode(true) as Element;
};

const motionIconSize = APP_CONFIG.MAPS.VEHICLE_EVENT_ICON_SIZE;
const eventIconSize = APP_CONFIG.MAPS.VEHICLE_EVENT_ICON_SIZE * 1.6;

export class RenderTripPoints extends RenderRoute {
  useDotsForStartEnd: boolean = false;

  constructor(params: any) {
    super(params);
    this.useDotsForStartEnd = params.useDotsForStartEnd ?? false;
  }
  render = async () => {
    const { props, tripIndex, sanitizedTripPoints, polylineConfig } = this;
    const map = this.props.mapRef.current.map;

    let waypointCount = 0;
    let viapointCount = 0;

    const { AdvancedMarkerElement } = (await google?.maps?.importLibrary(
      "marker"
    )) as any;

    // add waypoints
    sanitizedTripPoints.map((tripPoint, pointIndex) => {
      const isStart = pointIndex === 0;
      const isEnd = pointIndex === sanitizedTripPoints.length - 1; // && tripPoint.statuscode === VehicleStatusCode.STATUS_IGNITION_OFF; // disabled to let the red marker show on the last point on trips list map screen
      const isMotionEvent =
        tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_HEADING ||
        tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_MOVING ||
        tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_ENROUTE ||
        tripPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE ||
        tripPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE_1 ||
        tripPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE_2;
      // || tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_EN_ROUTE // typescript giving unintentional comparison error here probably because STATUS_MOTION_ENROUTE is equal to STATUS_MOTION_EN_ROUTE
      // limit the number of via points to 10 between two waypoints
      let isViaPoint = !isStart && !isEnd;
      if (!isStart && !isEnd) {
        if (viapointCount < MAX_VIAPOINTS) {
          isViaPoint = true;
          viapointCount++;
        } else {
          isViaPoint = false;
          waypointCount++;
          viapointCount = 0;
        }
      }
      if (pointIndex === sanitizedTripPoints.length - 1) isViaPoint = false; // last point cannot be a viapoint

      let icon = null;

      // add pushpins for start and end points
      if ((isStart || isEnd) && props.selectedTripIndex === null) {
        const anchorPoint = {
          x: APP_CONFIG.MAPS.VEHICLE_EVENT_STARTEND_ICON_SIZE * 0.5,
          y: APP_CONFIG.MAPS.VEHICLE_EVENT_STARTEND_ICON_SIZE * 0.8,
        };
        const parseSvgToDom = (svgString: string) => {
          const parser = new DOMParser();
          return parser.parseFromString(svgString, "image/svg+xml")
            .documentElement;
        };
        // Function to convert SVG to Data URL
        const parseSvgToDataUrl = (svgString: any) => {
          const svgBlob = new Blob([svgString], {
            type: "image/svg+xml",
          });
          return URL.createObjectURL(svgBlob);
        };
        //  const pinSvg = parseSvgToDom(icon);
        const markerContent = this.useDotsForStartEnd
          ? parseSvgToDom(
              `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="5" cy="5" r="5" fill="${isEnd ? "#D0021B" : "#009006"}"/>
        <circle cx="5" cy="5" r="4" stroke="white" stroke-width="1"/>
      </svg>`
            )
          : parseSvgToDom(
              isEnd
                ? svgIcons.RouteEndIconAsString
                : svgIcons.RouteStartIconAsString
            );
        const visible =
          props?.selectedTripIndex !== null &&
          tripIndex !== props?.selectedTripIndex
            ? false
            : true;
        // Create marker for start and end points
        const startStopMarker = new AdvancedMarkerElement({
          position: {
            lat: tripPoint.latitude,
            lng: tripPoint.longitude,
          },
          map: visible ? map : null,
          content: markerContent,
        });

        // add event listeners to the pushpin
        startStopMarker.addEventListener("click", () => {
          props.onDataPointPushpinClick?.(tripPoint);
        });

        // extra dot pushpin for start and end points
        if (APP_CONFIG.MAPS.TRIPS_SHOW_DOT_WITH_START_STOP_MARKERS) {
          // Convert SVG to Data URL
          const svgIcon = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="5" cy="5" r="5" fill="${isEnd ? "red" : "green"}"/>
                </svg>`;

          const dotSvg = parseSvgToDom(svgIcon);
          const isVisible =
            props?.selectedTripIndex !== null &&
            tripIndex !== props?.selectedTripIndex
              ? false
              : true;
          // Create Google Maps Marker
          const dotMarker = new AdvancedMarkerElement({
            position: {
              lat: tripPoint.latitude,
              lng: tripPoint.longitude,
            },
            map: isVisible ? map : null,
            content: dotSvg,
          });

          // add the dot marker to the objects
          const dotPushpinObject: TMPushpin = {
            tripIndex,
            recordId: tripPoint.recordid,
            marker: dotMarker,
            isStart,
            isEnd,
            pointData: tripPoint,
          };
          props.mapRef.current.objects.mPushpins.push(dotPushpinObject);
        }

        // set the marker in map elements so that it can be accessed later for hide/show
        this.setMapElements([
          ...(this.getMapElements() || []),
          startStopMarker,
        ]);

        // add the pushpin to the objects
        const pushpinObject: TMPushpin = {
          tripIndex,
          recordId: tripPoint.recordid,
          marker: startStopMarker,
          isStart,
          isEnd,
          pointData: tripPoint,
        };
        props.mapRef.current.objects.mPushpins.push(pushpinObject);
      } else if (
        props?.selectedTripIndex !== null &&
        tripIndex === props?.selectedTripIndex
      ) {
        const isCrashTrackImpactPoint =
          !!props.crashTrackImpactTimestamp &&
          tripPoint.timestamp === props.crashTrackImpactTimestamp &&
          !!APP_CONFIG.CRASHTRACK.USE_SPL_ICON;
        // heading is continuous, so it would defeat the icon cache on its
        // own. 5 degrees is finer than the arrow can show.
        const headingBucket =
          Math.round((tripPoint.heading || 0) / HEADING_BUCKET_DEGREES) *
          HEADING_BUCKET_DEGREES;
        icon = isMotionEvent
          ? // driving icon with orientation
            mapVehicleIconWrapped("driving", false, headingBucket)
          : // event icon
            isCrashTrackImpactPoint
            ? CrashTrackImpactIcon(APP_CONFIG.MAPS.VEHICLE_EVENT_ICON_SIZE)
            : mapVehicleEventIconWrapped(
                tripPoint.statuscode,
                false,
                eventIconSize
              );

        // Set anchor point based on motion or event status
        const anchorPoint = isMotionEvent
          ? { x: motionIconSize * 0.5, y: motionIconSize * 0.5 }
          : { x: eventIconSize * 0.5, y: eventIconSize * 0.8 };
        const isVisible =
          props?.selectedTripIndex !== null &&
          tripIndex !== props?.selectedTripIndex
            ? false
            : isMotionEvent && false // added false to hide motion events whenever applicable (per filter)
              ? true
              : statusCodeIsPinned(
                  props?.selectedStatusCodes,
                  tripPoint.statuscode
                );
        if (!isVisible) return;
        // crash-track's special icon is one-off; everything else repeats
        const iconCacheKey = isCrashTrackImpactPoint
          ? `crash|${tripPoint.timestamp}`
          : isMotionEvent
            ? `motion|${headingBucket}`
            : `event|${tripPoint.statuscode}`;
        const pinSvg = parsedIconFor(iconCacheKey, icon);
        // Create the marker
        const tripEventMarker = new AdvancedMarkerElement({
          position: {
            lat: tripPoint.latitude,
            lng: tripPoint.longitude,
          },
          map: isVisible ? map : null,
          content: pinSvg,
          ...(!!props.crashTrackImpactTimestamp &&
          tripPoint.timestamp === props.crashTrackImpactTimestamp
            ? // if zIndex this doesn't work, 'collisionBehavior: google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY,' can be used on other markers to make them low priority
              { zIndex: 99999 }
            : {}),
        });

        // Apply anchor (offset) using CSS transform
        // 3/26/2025 Disabled the transform styles below as this is causing the dot to be off the actual location
        // tripEventMarker.content.style.transform = `translate(-${anchorPoint.x}px, -${anchorPoint.y}px)`;
        // 4/4/2024 following style helps to position the marker correctly
        if (isMotionEvent) {
          // to position the enroute arrow icon correctly
          tripEventMarker.content.style.transform = "translateY(50%)";
        } else {
          // to position other event icons (white marker shape) correctly
          tripEventMarker.content.style.transform = "translateY(15%)";
        }

        // Animate the last reporting asset event icon
        const isLastEvent = pointIndex === sanitizedTripPoints.length - 1;
        if (isLastEvent) {
          // inject key frames
          if (!document.getElementById("last-event-animated-style")) {
            const styleEl = document.createElement("style");
            styleEl.id = "last-event-animated-style";
            styleEl.textContent = `
                  @keyframes lastEventBounce {
                  0% , 100% {transform: translateY(15%) scale(1);}
                  30% {transform:translateY(5%)  scale(1.3);}
                  50% {transform:translateY(15%)  scale(0.95);}
                  70% transform:translateY(10%)  scale(1.1);
                  }

                  .last-event-animated {
                  animation: lastEventBounce 1.2s ease-in-out infinite;
                  }
                  `;
            document.head.appendChild(styleEl);
          }
          tripEventMarker.content.classList.add("last-event-animated");
        }

        // Add click event listener
        tripEventMarker.addEventListener("click", () => {
          props.onDataPointPushpinClick?.(tripPoint);
        });

        // set the marker in map elements so that it can be accessed later for hide/show
        this.setMapElements([
          ...(this.getMapElements() || []),
          tripEventMarker,
        ]);

        // add the pushpin to the objects
        const pushpinObject: TMPushpin = {
          tripIndex,
          recordId: tripPoint.recordid,
          marker: tripEventMarker,
          isEvent: true,
          pointData: tripPoint,
        };
        props.mapRef.current.objects.mPushpins.push(pushpinObject);
      }

      return tripPoint;
    });
  };
}
